import React, { useState, useEffect } from "react";
import { Form, Spinner, Alert } from "react-bootstrap";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "config.js";

const CampaignDetails = ({ data, setData, nextStep, prevStep, isProcessing, setIsProcessing }) => {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [numbersLoading, setNumbersLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchLists = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(`${API_BASE_URL}/contact-lists`, {
        headers: authHeaders,
      });

      console.log("CampaignDetails fetch contact lists. count:", response.data.data?.length, "lists:", response.data.data);

      // Map native data to react-select options
      const mappedOptions = (response.data.data || []).map(list => ({
        value: list._id,
        label: list.listName,
        count: list.countContact || 0
      }));

      setLists(mappedOptions);
    } catch (error) {
      console.error("Error fetching Contact Lists:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableNumbers = async () => {
    try {
      setNumbersLoading(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const url = data.draftId
        ? `${API_BASE_URL}/phone-numbers/list?unassigned=true&excludeCampaignId=${data.draftId}`
        : `${API_BASE_URL}/phone-numbers/list?unassigned=true`;
      const response = await axios.get(url, { headers });
      
      console.log("CampaignDetails fetch phone numbers. count:", response.data.phoneNumbers?.length, "numbers:", response.data.phoneNumbers);

      if (response.data.success) {
        const mapped = response.data.phoneNumbers.map(n => ({
          value: n._id,
          label: `${n.phoneNumber} (${n.friendlyName || 'Unassigned'})`
        }));
        setAvailableNumbers(mapped);
      }
    } catch (err) {
      console.error("Error fetching available numbers:", err);
    } finally {
      setNumbersLoading(false);
    }
  };

  useEffect(() => {
    fetchLists();
    fetchAvailableNumbers();
  }, []);

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleListChange = (selectedOption) => {
    setData({
      ...data,
      contactListId: selectedOption ? selectedOption.value : null,
      contactListName: selectedOption ? selectedOption.label : "",
      contactListCount: selectedOption ? selectedOption.count : 0,
    });
  };

  const handleNumbersChange = (selectedOptions) => {
    const vals = selectedOptions ? selectedOptions.map(o => o.value) : [];
    setData({
      ...data,
      phoneNumbers: vals
    });
  };

  const handleContinue = () => {
    setErrorMsg("");
    if (!data.name || !data.name.trim()) {
      setErrorMsg("Campaign name is required.");
      return;
    }
    if (!data.contactListId) {
      setErrorMsg("Please select a target contact list.");
      return;
    }
    if (!data.phoneNumbers || data.phoneNumbers.length === 0) {
      setErrorMsg("Please select at least 1 phone number for the campaign.");
      return;
    }
    if (data.contactListCount > 1 && data.phoneNumbers.length < 2) {
      setErrorMsg("Bulk campaigns (more than 1 contact) require at least 2 active phone numbers.");
      return;
    }

    // Draft Saving logic (Enabled)
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      const draftPayload = {
        name: data.name,
        contactListId: data.contactListId,
        messageBody: data.messageTemplate || "Draft Message",
        campaignType: data.campaignType || "broadcast",
        complianceOption: data.complianceOption || "Reply STOP to unsubscribe",
        isDraft: true,
        draftId: data.draftId || null,
        phoneNumbers: data.phoneNumbers || [],
        messagesPerNumber: (data.messagesPerNumber !== "" && data.messagesPerNumber !== undefined) ? parseInt(data.messagesPerNumber, 10) : undefined,
      };

      axios.post(`${API_BASE_URL}/campaigns`, draftPayload, { headers: authHeaders })
        .then(res => {
          setIsProcessing(false);
          if (res.data.success && res.data.campaign) {
            setData({
              ...data,
              draftId: res.data.campaign._id,
              approvalStatus: res.data.campaign.approvalStatus || "none"
            });
            nextStep();
          } else {
            setErrorMsg("Failed to save campaign draft. Please try again.");
          }
        })
        .catch(err => {
          setIsProcessing(false);
          console.error("Error saving campaign draft:", err);
          setErrorMsg(err.response?.data?.message || "Failed to save campaign draft.");
        });
      return;
    } catch (err) {
      setIsProcessing(false);
      console.error("Error saving campaign draft:", err);
    }
  };

  return (
    <>
      <h5 style={{ fontWeight: 700, marginBottom: "25px" }}>Campaign Details</h5>

      {errorMsg && (
        <Alert variant="danger" className="mb-4">
          {errorMsg}
        </Alert>
      )}

      <Form>
        <Form.Group className="mb-4">
          <Form.Label style={{ fontWeight: "600", color: "#5E5E5E" }}>Campaign Name</Form.Label>
          <Form.Control
            type="text"
            name="name"
            value={data.name}
            onChange={handleChange}
            placeholder="Enter campaign name..."
            style={{ border: "2px solid #8E8E8E", borderRadius: "10px" }}
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label style={{ fontWeight: "600", color: "#5E5E5E" }}>Campaign Type</Form.Label>
          <div className="d-flex gap-5 align-items-center mt-2">
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", color: "#5E5E5E", cursor: "pointer", margin: 0 }}>
              <input
                type="radio"
                name="campaignType"
                id="campaignTypeBroadcast"
                value="broadcast"
                checked={data.campaignType === "broadcast"}
                onChange={handleChange}
                style={{
                  appearance: "none",
                  WebkitAppearance: "none",
                  width: "18px",
                  height: "18px",
                  border: data.campaignType === "broadcast" ? "2px solid #004992" : "2px solid #8E8E8E",
                  borderRadius: "50%",
                  outline: "none",
                  cursor: "pointer",
                  margin: 0,
                  transition: "all 0.15s ease-in-out",
                  background: data.campaignType === "broadcast" ? "radial-gradient(circle, #004992 40%, transparent 45%)" : "transparent"
                }}
              />
              A2P (Automated Broadcast)
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", color: "#5E5E5E", cursor: "pointer", margin: 0 }}>
              <input
                type="radio"
                name="campaignType"
                id="campaignTypeP2P"
                value="p2p"
                checked={data.campaignType === "p2p"}
                onChange={handleChange}
                style={{
                  appearance: "none",
                  WebkitAppearance: "none",
                  width: "18px",
                  height: "18px",
                  border: data.campaignType === "p2p" ? "2px solid #004992" : "2px solid #8E8E8E",
                  borderRadius: "50%",
                  outline: "none",
                  cursor: "pointer",
                  margin: 0,
                  transition: "all 0.15s ease-in-out",
                  background: data.campaignType === "p2p" ? "radial-gradient(circle, #004992 40%, transparent 45%)" : "transparent"
                }}
              />
              P2P (Peer-to-Peer / Click-to-Send)
            </label>
          </div>
        </Form.Group>

        <Form.Group className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <Form.Label className="m-0" style={{ fontWeight: "600", color: "#5E5E5E" }}>Target Contact List</Form.Label>
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              style={{ fontSize: "0.85rem", fontWeight: "600", color: "#004992", cursor: "pointer", border: "none", background: "none" }}
              onClick={fetchLists}
              disabled={isLoading}
            >
              <i className={`fas fa-sync-alt me-1 ${isLoading ? 'fa-spin' : ''}`}></i> Refresh Lists
            </button>
          </div>
          {isLoading ? (
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-muted ms-2">Loading your lists...</span>
            </div>
          ) : (
            <Select
              options={lists}
              value={lists.find(opt => opt.value === data.contactListId) || null}
              onChange={handleListChange}
              placeholder="Select a Contact List..."
              classNamePrefix="select"
              styles={{
                control: (base) => ({
                  ...base,
                  border: "2px solid #8E8E8E",
                  borderRadius: "10px",
                  minHeight: "42px",
                })
              }}
            />
          )}
        </Form.Group>

        <Form.Group className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <Form.Label className="m-0" style={{ fontWeight: "600", color: "#5E5E5E" }}>Routing Phone Numbers</Form.Label>
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              style={{ fontSize: "0.85rem", fontWeight: "600", color: "#004992", cursor: "pointer", border: "none", background: "none" }}
              onClick={fetchAvailableNumbers}
              disabled={numbersLoading}
            >
              <i className={`fas fa-sync-alt me-1 ${numbersLoading ? 'fa-spin' : ''}`}></i> Refresh Numbers
            </button>
          </div>
          {numbersLoading ? (
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-muted ms-2">Loading available numbers...</span>
            </div>
          ) : (
            <Select
              isMulti
              options={availableNumbers}
              value={availableNumbers.filter(opt => data.phoneNumbers?.includes(opt.value))}
              onChange={handleNumbersChange}
              placeholder="Select phone numbers to assign..."
              classNamePrefix="select"
              styles={{
                control: (base) => ({
                  ...base,
                  border: "2px solid #8E8E8E",
                  borderRadius: "10px",
                  minHeight: "42px",
                })
              }}
            />
          )}
        </Form.Group>

        {data.phoneNumbers && data.phoneNumbers.length > 1 && (
          <Form.Group className="mb-4">
            <Form.Label style={{ fontWeight: "600", color: "#5E5E5E" }}>Messages per Number Rotation</Form.Label>
            <Form.Control
              id="messagesPerNumber"
              type="number"
              name="messagesPerNumber"
              value={data.messagesPerNumber || ""}
              onChange={(e) => setData({ ...data, messagesPerNumber: e.target.value })}
              placeholder="e.g. 20 (Leave blank for simple rotation)"
              style={{ border: "2px solid #8E8E8E", borderRadius: "10px" }}
              min="1"
            />
            <Form.Text className="text-muted">
              How many messages each number sends before rotating to the next. Leave blank to rotate every message.
            </Form.Text>
          </Form.Group>
        )}

        <div className="d-flex justify-content-between mt-4">
          <button
            type="button"
            className="btn"
            style={{
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              borderRadius: "6px",
              padding: "8px 25px",
              fontWeight: "600",
              color: 'black'
            }}
            onClick={() => navigate("/admin/campaign-overview")}
          >
            Back
          </button>

          <button
            type="button"
            className="btn"
            disabled={isProcessing}
            style={{
              backgroundColor: isProcessing ? "#709bc6" : "#004992",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "8px 25px",
              fontWeight: "600",
            }}
            onClick={handleContinue}
          >
            {isProcessing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : "Continue"}
          </button>
        </div>
      </Form>
    </>
  );
};

export default CampaignDetails;